import LockIcon from "@mui/icons-material/Lock";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type Guid from "_shared/guids/guid";
import { useState } from "react";
import {
  useGetNetworkConnectionNoteQuery,
  useSaveNetworkConnectionNoteMutation,
} from "users/networking/connections/notes/_api/network-connection-note-api";
import { MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH } from "users/networking/connections/notes/_schemas/network-connection-note-schema";

/** The counter appears once the note is this close to the limit, rather than on every keystroke. */
const COUNTER_THRESHOLD = MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH - 200;

const EditorContainer = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const HeadingRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const NoteBody = styled(Typography)(() => ({
  overflowWrap: "anywhere",
  whiteSpace: "pre-wrap",
}));
/** Pushed to the far end of the heading row, so a saved note costs two lines and no more. */
const EditButton = styled(Button)(() => ({
  marginLeft: "auto",
}));
const ButtonRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
}));

export interface NetworkConnectionNoteEditorProps {
  connectionId: Guid;
}

/**
 * The caller's private note on one of their contacts (#1209).
 *
 * Only the caller can read it: the server answers every request with the caller's own note, and the
 * other party has a note of their own, or none. Saving empty text clears the note.
 *
 * It sits on the contact's profile, so it stays out of the way until it is used: a single "Add a
 * private note" link when there is none, the note as a field when there is one, and the editor only
 * once the person asks to write.
 */
export default function NetworkConnectionNoteEditor(props: Readonly<NetworkConnectionNoteEditorProps>) {
  const { connectionId } = props;
  const noteQuery = useGetNetworkConnectionNoteQuery({ connectionId });
  const [saveNote, saveResult] = useSaveNetworkConnectionNoteMutation();

  const [isEditing, setIsEditing] = useState(false);
  // null while the person has typed nothing since the editor opened.
  const [draft, setDraft] = useState<string | null>(null);

  // A draft belongs to one connection. The profile dialog can move to another contact without
  // remounting this editor, and an unsaved draft for the first must not be saved on the second.
  // React's "adjust state during render" pattern, so the second contact's first paint has no draft.
  const [draftConnectionId, setDraftConnectionId] = useState(connectionId.value);
  if (draftConnectionId !== connectionId.value) {
    setDraftConnectionId(connectionId.value);
    setDraft(null);
    setIsEditing(false);
  }

  const savedBody = noteQuery.data?.body ?? "";
  const text = draft ?? savedBody;
  const isTooLong = text.length > MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH;
  const isChanged = draft !== null && draft !== savedBody;
  const isBusy = noteQuery.isLoading || saveResult.isLoading;

  function closeEditor() {
    setDraft(null);
    setIsEditing(false);
  }

  async function save(body: string) {
    const cleared = body.trim().length === 0;
    try {
      await saveNote({ connectionId, body }).unwrap();
    } catch {
      enqueueSnackbar("Your note could not be saved. Try again.", { variant: "error" });
      return;
    }
    closeEditor();
    enqueueSnackbar(cleared ? "Note cleared" : "Note saved", { variant: "success" });
  }

  if (noteQuery.isLoading) return null;

  if (noteQuery.isError) {
    return (
      <EditorContainer aria-label="Private note">
        <Typography variant="body2" color="error">
          Your private note could not be loaded.
        </Typography>
      </EditorContainer>
    );
  }

  if (!isEditing && savedBody.length === 0) {
    return (
      <EditorContainer aria-label="Private note">
        <HeadingRow>
          <Button
            variant="text"
            size="small"
            startIcon={<LockIcon fontSize="small" />}
            onClick={() => setIsEditing(true)}
          >
            Add a private note
          </Button>
        </HeadingRow>
      </EditorContainer>
    );
  }

  const heading = (
    <>
      <LockIcon fontSize="small" color="action" aria-hidden />
      <Typography id="network-connection-note-heading" variant="subtitle2" component="h3">
        Private note
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Only you can see this
      </Typography>
    </>
  );

  if (!isEditing) {
    return (
      <EditorContainer aria-labelledby="network-connection-note-heading">
        <HeadingRow>
          {heading}
          <EditButton variant="text" size="small" onClick={() => setIsEditing(true)}>
            Edit note
          </EditButton>
        </HeadingRow>
        <NoteBody variant="body2">{savedBody}</NoteBody>
      </EditorContainer>
    );
  }

  let helperText: string | undefined;
  if (isTooLong) {
    helperText = `A note cannot exceed ${MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH} characters. Remove ${
      text.length - MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH
    } to save it.`;
  } else if (text.length > COUNTER_THRESHOLD) {
    helperText = `${text.length} / ${MAXIMUM_NETWORK_CONNECTION_NOTE_LENGTH}`;
  }

  return (
    <EditorContainer aria-labelledby="network-connection-note-heading">
      <HeadingRow>{heading}</HeadingRow>
      <TextField
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={isBusy}
        value={text}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="For example, where you met or how they prefer to be contacted."
        error={isTooLong}
        helperText={helperText}
        slotProps={{ htmlInput: { "aria-label": "Private note" } }}
      />
      <ButtonRow>
        {savedBody.length > 0 && (
          <Button variant="text" size="small" color="error" disabled={isBusy} onClick={() => void save("")}>
            Clear note
          </Button>
        )}
        <Button variant="text" size="small" disabled={isBusy} onClick={closeEditor}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!isChanged || isTooLong || isBusy}
          onClick={() => void save(text)}
        >
          Save note
        </Button>
      </ButtonRow>
    </EditorContainer>
  );
}
